/**
 * printUtils.ts
 *
 * Shared utility for the html2canvas → print window pipeline.
 *
 * WHY we use `size: auto <orientation>` and cm units (not px):
 *  - `@page { size: Xcm Ycm }` is often IGNORED by printer drivers that have
 *    Letter or A4 loaded. When ignored the browser scales content to fit the
 *    loaded paper, destroying every carefully positioned cm coordinate.
 *  - Instead we use `@page { size: auto portrait }` (or landscape) which tells
 *    the browser "use whatever paper is loaded but in this orientation".
 *  - We then size the image in CSS `cm` units. CSS `cm` is ALWAYS a physical
 *    centimetre on the printed page (the W3C spec mandates 1in = 96px, so
 *    1cm = 37.7953px, and those map 1-to-1 to physical cm when printing).
 *  - The captured PNG fills the img element which is exactly pageWidth x pageHeight
 *    in physical cm. The printer prints it at that exact size from position (0,0)
 *    on the paper, no scaling, no repositioning.
 */

/**
 * Parse a CSS dimension string to centimetres for comparison.
 * Exported so callers that generate raw HTML (not blobs) can still derive
 * orientation using the same logic.
 */
export function parseToCm(dim: string): number {
    const trimmed = dim.trim();
    const num = parseFloat(trimmed);
    if (isNaN(num)) return 0;
    if (trimmed.endsWith('mm')) return num / 10;
    if (trimmed.endsWith('cm')) return num;
    if (trimmed.endsWith('in')) return num * 2.54;
    if (trimmed.endsWith('px')) return num / 37.7953;
    if (trimmed.endsWith('pt')) return num * 0.0352778;
    return num; // bare number treated as cm
}

/**
 * Open a print window for raw HTML content, printing it natively as vector text.
 *
 * @param htmlContent HTML string to render
 * @param pageWidth   CSS dimension string from printConfig, e.g. "19cm"
 * @param pageHeight  CSS dimension string from printConfig, e.g. "8cm"
 * @param title       Print window title
 * @param onPrinted   Callback fired immediately after window.print() is called
 */
export function openPrintWindowHTML(
    htmlContent: string,
    pageWidth: string,
    pageHeight: string,
    title: string,
    onPrinted?: () => void,
): void {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
        throw new Error('Please allow popups for printing');
    }

    const wCm = parseToCm(pageWidth);
    const hCm = parseToCm(pageHeight);
    const orientation = wCm > hCm ? 'landscape' : 'portrait';

    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${title}</title>
  <link rel="stylesheet" href="/fonts/fonts.css">
  <style>
    @page {
      size: auto ${orientation};
      margin: 0;
    }

    *, *::before, *::after {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    html, body {
      width: ${pageWidth};
      background: #fff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      color-adjust: exact;
    }
  </style>
</head>
<body>
  ${htmlContent}
</body>
</html>`);

    printWindow.document.close();

    printWindow.onload = () => {
        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            if (onPrinted) onPrinted();
            setTimeout(() => {
                printWindow.close();
            }, 100);
            printWindow.onafterprint = () => {
                printWindow.close();
            };
        }, 300);
    };
}

/**
 * Open a print window for a canvas-captured blob.
 *
 * @param blob       PNG blob produced by canvas.toBlob()
 * @param pageWidth  CSS dimension string from printConfig, e.g. "19cm"
 * @param pageHeight CSS dimension string from printConfig, e.g. "8cm"
 * @param title      Print window title
 * @param onPrinted  Callback fired immediately after window.print() is called
 */
export function openPrintWindow(
    blob: Blob | Blob[],
    pageWidth: string,
    pageHeight: string,
    title: string,
    onPrinted?: () => void,
): void {
    const blobs = Array.isArray(blob) ? blob : [blob];
    const imageUrls = blobs.map(b => URL.createObjectURL(b));
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
        imageUrls.forEach(url => URL.revokeObjectURL(url));
        throw new Error('Please allow popups for printing');
    }

    // Derive orientation from config dimensions so the printer rotates correctly.
    // width > height -> landscape (e.g. passbook 19cm x 8cm)
    // height >= width -> portrait (e.g. A4 certificate 21cm x 29.7cm)
    const wCm = parseToCm(pageWidth);
    const hCm = parseToCm(pageHeight);
    const orientation = wCm > hCm ? 'landscape' : 'portrait';

    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${title}</title>
  <style>
    /*
     * Do NOT try to change the paper size with @page { size: Xcm Ycm }.
     * Printer drivers routinely ignore custom sizes and fall back to Letter/A4,
     * which causes the browser to SCALE the content to fit, ruining positions.
     *
     * Instead: size: auto ${orientation} keeps whatever paper is loaded but
     * rotates it to match the configured page dimensions.
     * margin: 0 removes all browser-added whitespace so image starts at (0,0).
     */
    @page {
      size: auto ${orientation};
      margin: 0;
    }

    *, *::before, *::after {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    html, body {
      width: ${pageWidth};
      background: #fff;
      /*
       * Prevent the browser from lightening images/backgrounds when printing.
       * Without this Chrome may grey out or shrink the image.
       */
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      color-adjust: exact;
    }

    .page-container {
      width: ${pageWidth};
      height: ${pageHeight};
      overflow: hidden;
      position: relative;
      page-break-after: always;
    }

    .page-container:last-child {
      page-break-after: avoid;
    }

    img {
      display: block;
      /*
       * CSS cm is ALWAYS a physical centimetre on printed output.
       * This ensures the image occupies exactly pageWidth x pageHeight on paper
       * regardless of the monitor resolution or system DPI scaling.
       */
      width: ${pageWidth};
      height: ${pageHeight};
    }
  </style>
</head>
<body>
  ${imageUrls.map((url, index) => `
    <div class="page-container">
      <img src="${url}" alt="${title} - Page ${index + 1}">
    </div>
  `).join('')}
</body>
</html>`);

    printWindow.document.close();

    printWindow.onload = () => {
        // Short delay to let the image fully decode before the print dialog opens
        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            if (onPrinted) onPrinted();
            setTimeout(() => {
                printWindow.close();
                imageUrls.forEach(url => URL.revokeObjectURL(url));
            }, 100);
            printWindow.onafterprint = () => {
                printWindow.close();
                imageUrls.forEach(url => URL.revokeObjectURL(url));
            };
        }, 300);
    };
}
